/* eslint-disable max-classes-per-file */
import { Directive, inject, Input, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, isFormControl, NgControl } from '@angular/forms';
import {Subscription} from 'rxjs';
import { patchObjectFn } from 'webcommon/shared';
import {ApBaseControlValueAccessor} from './ApBaseControlValueAccessor';
import { ToValue } from './ApBaseCVAControl';


// Inheriting from this does NOT make a normal CVA, it's really just a pass-thru for a FormControl
// to an inner element.
// Because of that, the CVA methods are all noops.
// This originally did similar logic to ApBaseCVAControl, where it had a separate inner FormControl,
// that would communicate and sync back and forth with the outer one,
// but then I figured it wasn't really accomplishing anything extra that couldn't already be done with just a single FormControl.
// This also means that tests expecting writeValue and other CVA methods to have the normal CVA behavior won't work.
// But you can set the value and disabled and stuff on the actual FormControl bound to this component and it will work fine.
@Directive()
export abstract class ApBaseCVAFormControl<TFormControl extends FormControl>
extends ApBaseControlValueAccessor<ToValue<TFormControl>, ToValue<TFormControl>>
implements OnDestroy {
  innerControl?: TFormControl;
  private _innerControlSub?: Subscription;

  protected override onOuterControlChanged(outerControl: AbstractControl) {
    super.onOuterControlChanged(outerControl);

    // for a single FormControl representing the value of this component,
    // we don't really need to do any setup, just use the outerControl
    if (!this.isValidFormControl(outerControl)) {
      throw new Error('no FormControl attached to element via formControl, formControlName, or ngModel');
    }
    this.setupInnerControl(outerControl);
  }

  protected setupInnerControl(innerControl: TFormControl): void {
    this._innerControlSub?.unsubscribe();
    this.innerControl = innerControl;
    this._innerControlSub = this.setupInnerControlSubs(innerControl);
  }

  // this is just a convenience method so that the child class doesn't have to do the boilerplate
  // to manage subscriptions if the FormControl changes
  protected setupInnerControlSubs(_innerControl: TFormControl): Subscription | undefined {
    return undefined;
  }

  // this is necessary to properly update the value of the [(ngModel)], and emit the ngModelChange event
  // on this component, when it changes in the innerControl
  _viewToModelUpdate(value: ToValue<TFormControl>) {
    this.ngControlDirective?.viewToModelUpdate(value);
  }

  override ngOnDestroy(): void {
    this._innerControlSub?.unsubscribe();
    this._innerControlSub = undefined;
    super.ngOnDestroy();
  }

  private isValidFormControl(control: AbstractControl): control is TFormControl {
    return isFormControl(control);
  }
}

// This directive enhances [formControl] and [ngModel] to
// to allow it to hook up with ApBaseCVAFormControl easier.
// The alternative of hooking into the valueChanges event on the innerControl can be problematic because
// if it was attached to the ApBaseCVAFormControl via ngModel, then there will be valueChanges events coming from the model
// and the view, and we only want to do viewToModelUpdate when coming from the view.
@Directive({
  selector: '[apCVAViewToModelChange][formControl],[apCVAViewToModelChange][ngModel]',
})
export class ApCVAViewToModelChangeDirective<TFormControl extends FormControl>
implements OnDestroy {
  @Input() apCVAViewToModelChange?: ApBaseCVAFormControl<TFormControl>;

  readonly ngControlDirective: NgControl = inject(NgControl, {
    self: true,
  });

  readonly teardown = new Subscription();

  constructor(
  ) {
    this.teardown.add(
      patchObjectFn(this.ngControlDirective, 'viewToModelUpdate', (originalFn) => {
        return (newValue) => {
          originalFn(newValue);
          this.apCVAViewToModelChange?._viewToModelUpdate(newValue);
        };
      })
    );
  }

  ngOnDestroy() {
    this.teardown.unsubscribe();
  }
}
